import { motion, useScroll, useSpring, useTransform } from "framer-motion";

const RoseImg = ({ p, start, x, y, w, rotate = 0, swayDelay = 0 }) => {
  const scale = useTransform(p, [start, start + 0.055], [0, 1]);
  const opacity = useTransform(p, [start, start + 0.03], [0, 1]);
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate})`}>
      <motion.g
        style={{ scale, opacity, transformBox: "fill-box", transformOrigin: "50% 60%" }}
      >
        <motion.g
          animate={{ rotate: [0, 2.5, 0, -2.5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: swayDelay }}
          style={{ transformBox: "fill-box", transformOrigin: "50% 60%" }}
        >
          <image href="/assets/rose.png" x={-w / 2} y={-w / 2} width={w} height={w} />
        </motion.g>
      </motion.g>
    </g>
  );
};

const Bud = ({ p, start, x, y, flip = false }) => {
  const scale = useTransform(p, [start, start + 0.05], [0, 0.55]);
  const opacity = useTransform(p, [start, start + 0.02], [0, 1]);
  return (
    <g transform={`translate(${x} ${y})${flip ? " scale(-1 1)" : ""}`}>
      <motion.g
        style={{ scale, opacity, transformBox: "fill-box", transformOrigin: "50% 80%" }}
      >
        <path d="M0 2 C 1 10 2 16 3 22" fill="none" stroke="#A8BE9A" strokeWidth="1.5" />
        <path
          d="M0 0 C -7 -2 -8 -14 0 -21 C 8 -14 7 -2 0 0 Z"
          fill="#E3A6B2"
          stroke="#D88C9A"
          strokeWidth="1"
        />
        <path d="M0 0 C -3 -5 -3 -11 0 -17" fill="none" stroke="#C57B89" strokeWidth="0.9" />
        <path d="M-3 1 C -6 3 -7 6 -6 9" fill="none" stroke="#A8BE9A" strokeWidth="1" />
        <path d="M3 1 C 6 3 7 6 6 9" fill="none" stroke="#A8BE9A" strokeWidth="1" />
      </motion.g>
    </g>
  );
};

const Leaf = ({ p, range, x, y, flip = false, small = false }) => {
  const scale = useTransform(p, range, [0, small ? 0.7 : 1]);
  const opacity = useTransform(p, range, [0, 1]);
  return (
    <g transform={`translate(${x} ${y})${flip ? " scale(-1 1)" : ""}`}>
      <motion.g
        style={{ scale, opacity, transformBox: "fill-box", transformOrigin: "0% 50%" }}
      >
        <path
          d="M0 0 C 6 -8 15 -12 23 -10 C 28 -8.5 31 -5 33 -1.5 C 27 4 15 8 5 3 C 2.5 2 1 1 0 0 Z"
          fill="#CBDFC1"
          stroke="#9DBB92"
          strokeWidth="1.1"
        />
        <path d="M2 -0.5 C 12 -6 22 -7 30 -3" fill="none" stroke="#9DBB92" strokeWidth="0.7" />
        <path d="M10 -3.5 L12 1 M18 -4.5 L20 0" fill="none" stroke="#9DBB92" strokeWidth="0.55" />
      </motion.g>
    </g>
  );
};

const TieBow = ({ p }) => {
  const scale = useTransform(p, [0.9, 0.98], [0, 1]);
  const opacity = useTransform(p, [0.9, 0.94], [0, 1]);
  return (
    <g transform="translate(57 916)">
      <motion.g
        style={{ scale, opacity, transformBox: "fill-box", transformOrigin: "50% 50%" }}
      >
        <path
          d="M0 0 c-9 -7 -18 -5 -16 2 c2 6 11 5 16 -2z"
          fill="#F3D9DC"
          stroke="#D4AF37"
          strokeWidth="1.5"
        />
        <path
          d="M0 0 c9 -7 18 -5 16 2 c-2 6 -11 5 -16 -2z"
          fill="#F3D9DC"
          stroke="#D4AF37"
          strokeWidth="1.5"
        />
        <path d="M-2 2 c-3 7 -7 12 -12 15" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M2 2 c3 7 7 12 12 15" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="0" cy="0" r="2.6" fill="#D4AF37" />
      </motion.g>
    </g>
  );
};

export default function RoseVine() {
  const { scrollYProgress } = useScroll();
  const p = useSpring(scrollYProgress, { stiffness: 55, damping: 20 });
  const stem1 = useTransform(p, [0.1, 0.85], [0, 1]);
  const stem2 = useTransform(p, [0.16, 0.9], [0, 1]);
  const stem3 = useTransform(p, [0.22, 0.95], [0, 1]);
  const stem4 = useTransform(p, [0.28, 0.97], [0, 1]);
  const stem5 = useTransform(p, [0.34, 1], [0, 1]);

  return (
    <div
      className="fixed inset-y-0 right-0 md:right-4 z-30 pointer-events-none w-14 md:w-36 opacity-50 md:opacity-100"
      aria-hidden="true"
      data-testid="rose-vine"
    >
      <motion.div
        className="w-full h-full"
        style={{ transformOrigin: "50% 0%" }}
        animate={{ rotate: [0, 1.1, 0, -1.1, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 120 1000" preserveAspectRatio="xMidYMin meet" className="w-full h-full">
          <motion.path
            d="M62 80 C 70 200 50 340 60 470 C 68 600 52 720 58 850 C 61 890 56 906 57 920"
            fill="none"
            stroke="#A8BE9A"
            strokeWidth="2.4"
            strokeLinecap="round"
            style={{ pathLength: stem1 }}
          />
          <motion.path
            d="M36 116 C 44 240 66 360 56 490 C 50 620 64 740 58 860 C 56 892 57 906 57 918"
            fill="none"
            stroke="#BCD0AF"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ pathLength: stem2 }}
          />
          <motion.path
            d="M88 120 C 96 250 56 370 62 500 C 66 630 52 750 58 868 C 60 896 57 908 57 918"
            fill="none"
            stroke="#BCD0AF"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ pathLength: stem3 }}
          />
          <motion.path
            d="M48 150 C 53 270 62 390 55 520 C 51 640 60 760 56 875 C 55 898 56 908 57 916"
            fill="none"
            stroke="#A8BE9A"
            strokeWidth="1.8"
            strokeLinecap="round"
            style={{ pathLength: stem4 }}
          />
          <motion.path
            d="M74 142 C 81 270 54 395 60 525 C 64 645 52 765 58 880 C 59 900 57 910 57 917"
            fill="none"
            stroke="#BCD0AF"
            strokeWidth="1.8"
            strokeLinecap="round"
            style={{ pathLength: stem5 }}
          />

          <Leaf p={p} range={[0.12, 0.17]} x={68} y={150} small />
          <Leaf p={p} range={[0.13, 0.19]} x={66} y={172} small />
          <Leaf p={p} range={[0.17, 0.23]} x={50} y={208} flip small />
          <Leaf p={p} range={[0.19, 0.25]} x={44} y={240} flip small />
          <Leaf p={p} range={[0.22, 0.28]} x={64} y={268} />
          <Leaf p={p} range={[0.25, 0.31]} x={66} y={310} small />
          <Leaf p={p} range={[0.28, 0.34]} x={48} y={348} flip />
          <Leaf p={p} range={[0.35, 0.41]} x={63} y={428} />
          <Leaf p={p} range={[0.42, 0.48]} x={50} y={508} flip />
          <Leaf p={p} range={[0.45, 0.51]} x={46} y={470} flip small />
          <Leaf p={p} range={[0.5, 0.56]} x={62} y={588} />
          <Leaf p={p} range={[0.53, 0.59]} x={64} y={550} small />
          <Leaf p={p} range={[0.57, 0.63]} x={50} y={662} flip />
          <Leaf p={p} range={[0.6, 0.66]} x={48} y={700} flip small />
          <Leaf p={p} range={[0.64, 0.7]} x={61} y={737} />
          <Leaf p={p} range={[0.67, 0.73]} x={62} y={775} small />
          <Leaf p={p} range={[0.71, 0.77]} x={51} y={807} flip />
          <Leaf p={p} range={[0.78, 0.84]} x={60} y={866} />
          <Leaf p={p} range={[0.82, 0.87]} x={52} y={892} flip small />

          <TieBow p={p} />

          <RoseImg p={p} start={0.04} x={60} y={48} w={58} swayDelay={0} />
          <RoseImg p={p} start={0.055} x={80} y={62} w={30} rotate={24} swayDelay={0.6} />
          <RoseImg p={p} start={0.07} x={32} y={86} w={42} rotate={-18} swayDelay={1.2} />
          <RoseImg p={p} start={0.085} x={90} y={90} w={40} rotate={16} swayDelay={1.8} />
          <RoseImg p={p} start={0.1} x={48} y={106} w={38} rotate={-6} swayDelay={2.4} />
          <RoseImg p={p} start={0.11} x={74} y={112} w={36} rotate={8} swayDelay={3} />
          <RoseImg p={p} start={0.12} x={22} y={122} w={30} rotate={-28} swayDelay={3.6} />
          <RoseImg p={p} start={0.125} x={99} y={128} w={30} rotate={26} swayDelay={4.2} />
          <RoseImg p={p} start={0.13} x={60} y={130} w={34} rotate={2} swayDelay={4.8} />
          <Bud p={p} start={0.16} x={36} y={172} flip />
          <Bud p={p} start={0.2} x={86} y={190} />
        </svg>
      </motion.div>
    </div>
  );
}
